import { PackageLine, ValidationError, DimensionUnit, WeightUnit } from '@freight-cbm/shared-types';

const DIMENSION_THRESHOLDS: Record<DimensionUnit, { max: number; warn: number }> = {
  [DimensionUnit.Cm]: { max: 2000, warn: 500 },
  [DimensionUnit.Mm]: { max: 20000, warn: 5000 },
  [DimensionUnit.M]: { max: 20, warn: 5 },
  [DimensionUnit.In]: { max: 800, warn: 200 },
  [DimensionUnit.Ft]: { max: 65, warn: 17 },
};

const WEIGHT_THRESHOLDS: Record<WeightUnit, { max: number; warn: number }> = {
  [WeightUnit.Kg]: { max: 50000, warn: 10000 },
  [WeightUnit.Lb]: { max: 100000, warn: 22000 },
};

export function validatePackageLine(line: PackageLine): ValidationError[] {
  const errors: ValidationError[] = [];

  for (const field of ['length', 'width', 'height'] as const) {
    const value = line[field];
    const thresholds = DIMENSION_THRESHOLDS[line.dimUnit];
    if (thresholds) {
      if (value <= 0) {
        errors.push({
          packageId: line.id,
          field,
          message: `${field} must be positive`,
          severity: 'error',
        });
      } else if (value > thresholds.warn) {
        errors.push({
          packageId: line.id,
          field,
          message: `${field} seems unusually large for ${line.dimUnit} — check unit`,
          severity: 'warning',
        });
      }
    }
  }

  const wt = WEIGHT_THRESHOLDS[line.weightUnit];
  if (wt) {
    if (line.weight <= 0) {
      errors.push({
        packageId: line.id,
        field: 'weight',
        message: 'Weight must be positive',
        severity: 'error',
      });
    } else if (line.weight > wt.warn) {
      errors.push({
        packageId: line.id,
        field: 'weight',
        message: `Weight seems unusually large for ${line.weightUnit} — check unit`,
        severity: 'warning',
      });
    }
  }

  if (line.quantity < 1) {
    errors.push({
      packageId: line.id,
      field: 'quantity',
      message: 'Quantity must be ≥ 1',
      severity: 'error',
    });
  } else if (line.quantity > 10000) {
    errors.push({
      packageId: line.id,
      field: 'quantity',
      message: 'Quantity exceeds practical limit',
      severity: 'warning',
    });
  }

  return errors;
}

export function validateShipment(
  packages: PackageLine[],
  divisor: number,
): ValidationError[] {
  const errors: ValidationError[] = [];

  if (divisor <= 0) {
    errors.push({
      packageId: '',
      field: 'dimDivisor',
      message: 'DIM divisor must be greater than 0',
      severity: 'error',
    });
  }

  for (const pkg of packages) {
    errors.push(...validatePackageLine(pkg));
  }

  return errors;
}
